import React, { useState, useRef, memo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import useDropdownPosition from '../../../hooks/useDropdownPosition';

/**
 * Determines if text should be light or dark based on background color
 * Uses relative luminance calculation for accessibility
 */
const getContrastTextColor = (hexColor) => {
  if (!hexColor) return '#6c757d';
  
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate relative luminance (using sRGB formula)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return dark text for light backgrounds, white for dark
  return luminance > 0.5 ? '#333333' : '#ffffff';
};

/**
 * PriorityCell - Priority dropdown cell with color coding
 * 
 * Props:
 * - value: Display value (priority name)
 * - priorityId: Current priority ID
 * - rowIndex: Index of the row in the data array
 * - rowId: Unique ID of the row (for saving)
 * - columnId: ID of the column (for update callback)
 * - priorities: Array of available priorities [{id, priority_name, color_code}]
 * - hasEditPermission: Whether the user can edit this cell
 * - onUpdate: Callback to update local data (rowIndex, columnId, value)
 * - onSave: Callback to save to backend (rowId, columnId, oldValue, newValue) -> Promise
 * - isRetrying: Whether the save is being retried due to lock conflict
 */
const PriorityCell = memo(({ 
  value, 
  priorityId, 
  rowIndex, 
  rowId, 
  columnId = 'priority',
  priorities = [],
  hasEditPermission = false,
  onUpdate,
  onSave,
  isRetrying = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const triggerRef = useRef(null);
  const getPosition = useDropdownPosition({ menuHeight: 200, menuWidth: 150 });

  const handlePriorityChange = useCallback((newPriorityId) => {
    if (newPriorityId === priorityId) {
      setIsOpen(false);
      return;
    }
    
    setIsOpen(false);
    setIsSaving(true);
    
    // Update priority in the data immediately (using rowId for correct lookup in filtered/sorted data)
    if (onUpdate) {
      onUpdate(rowId, columnId, newPriorityId);
    }
    
    // Fire-and-forget: save in background without blocking UI
    if (onSave) {
      onSave(rowId, columnId, priorityId, newPriorityId)
        .then(() => setIsSaving(false))
        .catch(error => {
          setIsSaving(false);
          console.error('Background save failed:', error);
        });
    }
  }, [priorityId, rowId, columnId, onUpdate, onSave]);

  const handleClick = useCallback(() => {
    if (hasEditPermission) {
      setIsOpen(!isOpen);
    }
  }, [hasEditPermission, isOpen]);

  const selectedPriority = priorities.find(p => p.id === priorityId);
  const displayValue = selectedPriority ? selectedPriority.priority_name : value || 'None';

  return (
    <div 
      style={{ 
        position: 'relative', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        width: '100%', 
        height: '100%',
        cursor: hasEditPermission ? 'pointer' : 'default'
      }}
      onClick={handleClick}
    >
      <div
        ref={triggerRef}
        style={{
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: 'bold',
          cursor: hasEditPermission ? 'pointer' : 'default',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          backgroundColor: selectedPriority?.color_code || '#f8f9fa',
          color: getContrastTextColor(selectedPriority?.color_code),
          minWidth: '80px',
          justifyContent: 'center'
        }}
        title={hasEditPermission ? "Click to change priority" : "View only - no edit permission"}
      >
        {displayValue}
        {(isSaving || isRetrying) && (
          <div 
            style={{
              width: '10px',
              height: '10px',
              border: `2px solid ${isRetrying ? '#ffe4c4' : getContrastTextColor(selectedPriority?.color_code)}`,
              borderTop: `2px solid ${isRetrying ? '#ff8c00' : 'transparent'}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}
            title={isRetrying ? 'Retrying save...' : 'Saving...'}
          ></div>
        )}
        {!isSaving && !isRetrying && hasEditPermission && <span>▼</span>}
      </div>
      
      {isOpen && hasEditPermission && createPortal(
        <>
          <div
            onClick={() => setIsOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998
            }}
          />
          {(() => {
            const pos = getPosition(triggerRef.current);
            return (
              <div
                style={{
                  position: 'fixed',
                  ...(pos.openUpward
                    ? { bottom: `${window.innerHeight - pos.top}px` }
                    : { top: `${pos.top}px` }),
                  ...(pos.right !== undefined
                    ? { right: `${pos.right}px` }
                    : { left: `${pos.left}px` }),
                  transform: pos.transform,
                  backgroundColor: 'white',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  zIndex: 9999,
                  minWidth: '150px',
                  maxHeight: `${Math.min(200, pos.maxHeight)}px`,
                  overflowY: 'auto'
                }}
              >
                <div
                  onClick={() => handlePriorityChange(null)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    backgroundColor: !priorityId ? '#e7f3ff' : 'transparent',
                    borderBottom: '1px solid #f0f0f0',
                    color: '#333'
                  }}
                  onMouseEnter={(e) => {
                    if (!priorityId) return;
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                  }}
                  onMouseLeave={(e) => {
                    if (!priorityId) return;
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  None
                </div>
                {priorities.map(priority => (
                  <div
                    key={priority.id}
                    onClick={() => handlePriorityChange(priority.id)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      backgroundColor: priorityId === priority.id ? '#e7f3ff' : 'transparent',
                      borderBottom: '1px solid #f0f0f0',
                      color: '#333'
                    }}
                    onMouseEnter={(e) => {
                      if (priorityId === priority.id) return;
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      if (priorityId === priority.id) return;
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '2px',
                        backgroundColor: priority.color_code || '#ccc'
                      }}
                    />
                    {priority.priority_name}
                  </div>
                ))}
              </div>
            );
          })()}
        </>,
        document.body
      )}
    </div>
  );
});

PriorityCell.displayName = 'PriorityCell';

export default PriorityCell;